import { useQuery } from '@tanstack/react-query';
import { Card, Disclosure, Status, formatDatumZeit } from '@elbklar/ui';

import { radarApi } from '@/lib/api';
import type { RadarRun } from '@/types';

const KIND_TEXT: Record<string, string> = { monitor: 'Prüfung', discovery: 'Suche' };
const TRIGGER_TEXT: Record<string, string> = { cron: 'geplant', manual: 'von Hand' };
/**
 * Nur die sprechenden Zähler aus `stats`, je Art des Laufs — Token-Zahlen
 * (`usage`, `input_tokens`) gehören in die Kostenkontrolle, nicht in diese
 * Zeile. Die Schlüssel stehen in `MonitorStats` (`services/radar_monitor.py`)
 * und `DiscoveryStats` (`services/radar_discovery.py`); bis D2 kannte die
 * Zeile nur die der Prüfung, eine Suche stand ohne Zahlen da.
 */
type Zaehler = Record<string, string | [string, string]>;

const PRUEFUNG_ZAEHLER: Zaehler = {
  checked: 'geprüft',
  changed: 'geändert',
  relevant_changes: 'relevant',
  expired: 'abgelaufen',
  deadline_notices: ['Frist-Hinweis', 'Frist-Hinweise'],
  manual_checks_due: ['Prüfauftrag', 'Prüfaufträge'],
  blocked: 'gesperrt',
  dead: 'tot',
  unreadable: 'unlesbar',
  assessment_errors: ['Bewertungsfehler', 'Bewertungsfehler'],
};

const SUCHE_ZAEHLER: Zaehler = {
  categories: ['Art', 'Arten'],
  candidates: ['Kandidat', 'Kandidaten'],
  duplicates: 'schon bekannt',
  new: 'neu',
  watched: 'beobachtet',
  discarded: 'verworfen',
  unreadable: 'unlesbar',
  search_errors: ['Suchfehler', 'Suchfehler'],
  assessment_errors: ['Bewertungsfehler', 'Bewertungsfehler'],
};

function dauerText(run: RadarRun): string {
  if (!run.finished_at) return '';
  const sekunden = Math.round(
    (new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000
  );
  if (sekunden < 60) return `${sekunden} s`;
  return `${Math.round(sekunden / 60)} min`;
}

function statsText(run: RadarRun): string {
  const zaehler = run.kind === 'discovery' ? SUCHE_ZAEHLER : PRUEFUNG_ZAEHLER;
  const stats = run.stats;
  return Object.entries(zaehler)
    .map(([schluessel, label]) => [label, stats[schluessel]] as const)
    .filter((paar): paar is [string | [string, string], number] => typeof paar[1] === 'number' && paar[1] > 0)
    .map(([label, wert]) => {
      const text = typeof label === 'string' ? label : label[wert === 1 ? 0 : 1];
      return `${wert} ${text}`;
    })
    .join(' · ');
}

/**
 * Die letzten Läufe des Radars, eingeklappt am Fuß der Seite.
 *
 * Lädt erst beim Aufklappen (der Inhalt der `Disclosure` entsteht erst dann)
 * und fragt nur nach, solange ein Lauf aktiv ist — so sieht man nach „Jetzt
 * prüfen“, wann das Ergebnis da ist.
 */
export function LaeufeBlock() {
  return (
    <Disclosure label="Letzte Läufe">
      <LaeufeListe />
    </Disclosure>
  );
}

function LaeufeListe() {
  const laeufe = useQuery({
    queryKey: ['radar', 'runs'],
    queryFn: () => radarApi.runs(10),
    refetchInterval: (query) => (query.state.data?.some((run) => run.status === 'laufend') ? 10_000 : false),
  });

  return (
    <Card flush>
      {laeufe.isLoading ? (
        <p className="p-4 text-muted-foreground">Läufe werden geladen …</p>
      ) : (laeufe.data ?? []).length === 0 ? (
        <p className="p-4 text-muted-foreground">
          Noch kein Lauf. „Jetzt prüfen“ stößt den ersten an — der merkt sich nur den Stand der Quellen; erst der zweite
          meldet Änderungen.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {(laeufe.data ?? []).map((run) => (
            <li key={run.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
              {/* Wort und Ton aus `STATUS` (`lauf_…`); Unbekanntes zeigt den Rohwert. */}
              <Status status={`lauf_${run.status}`} />
              <span className="font-medium">{KIND_TEXT[run.kind] ?? run.kind}</span>
              <span className="text-muted-foreground">{TRIGGER_TEXT[run.trigger] ?? run.trigger}</span>
              <span className="tabular text-muted-foreground">{formatDatumZeit(run.started_at)}</span>
              {dauerText(run) && <span className="text-muted-foreground">{dauerText(run)}</span>}
              {run.status !== 'fehler' && statsText(run) && <span className="text-muted-foreground">{statsText(run)}</span>}
              {run.error && <span className="text-danger">{run.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
